import {
  HasuraDataTableColumnDef,
  DataTableEx,
  DataTableExProps,
  DataTableExRef,
  FormInput,
  DateInput,
  SelectInput,
  FormTabs,
  FormTab,
  Grid,
  FormGetter,
  NotificationsContext,
  AutocompleteInput,
  ConfigurationContext,
} from '@kirz/mui-admin';
import { Add, Refresh as RefreshIcon } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import React, {
  forwardRef,
  Ref,
  useContext,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';

import { Counter } from './NavigationDrawer';
import { WebFormUsersTable } from './WebFormUsersTable';

export const WebFormsTable = forwardRef(
  (
    props: Partial<DataTableExProps> & {
      id: string;
      filterColumns?: (
        columns: HasuraDataTableColumnDef[],
      ) => HasuraDataTableColumnDef[];
    },
    ref: Ref<DataTableExRef>,
  ) => {
    const { filterColumns, ...rest } = props;
    const tableRef = useRef<DataTableExRef>(null);
    const usersTableRef = useRef<DataTableExRef>(null);

    useImperativeHandle(ref, () => tableRef.current!, []);
    const { showPrompt, showAlert } = useContext(NotificationsContext);
    const { hasura } = useContext(ConfigurationContext);

    const columns = useMemo<HasuraDataTableColumnDef[]>(
      () =>
        (filterColumns || ((x: HasuraDataTableColumnDef[]) => x))([
          {
            field: 'name',
            headerName: 'Naam',
            flex: 1,
            renderCell: (params) => {
              return (
                <Typography variant="body2">
                  <Counter
                    color="error"
                    query={{
                      type: 'subscription',
                      aggregation: true,
                      source: 'clientWebForm',
                      selection: 'aggregate { count }',
                      where: {
                        isFilled: { _eq: true },
                        isProcessed: { _eq: false },
                        webFormId: { _eq: params.row.id },
                      },
                    }}
                    sx={{ mr: 1 }}
                  />
                  {params.row.name}
                </Typography>
              );
            },
          },
          {
            field: 'deadline',
            headerName: 'Deadline',
            flex: 1,
            type: 'date',
          },
          {
            field: 'reminder1Date',
            headerName: 'Herinnering',
            flex: 1,
            type: 'date',
          },
          {
            field: 'reminder2Date',
            headerName: 'Herinnering 2',
            flex: 1,
            type: 'date',
          },
          {
            field: 'reminder3Date',
            headerName: 'Herinnering 3',
            flex: 1,
            type: 'date',
          },
          { field: 'createdAt', headerName: 'Aangemaakt op', type: 'date' },
        ]),
      [filterColumns],
    );

    return (
      <DataTableEx
        {...rest}
        ref={tableRef}
        source="webForm"
        columns={columns}
        persistStateMode="query"
        sortBy={rest.sortBy ?? { field: 'createdAt', sort: 'desc' }}
        editable={{
          onEdit(row) {
            tableRef.current?.openFormDialog(row);
          },
        }}
        formTitle={(isNew) =>
          isNew ? 'Nieuwe Formulier' : 'Formulier bewerken'
        }
        formDialogProps={{
          ...rest.formDialogProps,
          maxWidth: 'md',
          formProps: {
            grid: false,
          },
        }}
      >
        <FormTabs
          tabs={[
            { id: 'main', label: 'Algemeen' },
            { id: 'users', label: 'Gebruikers' },
            { id: 'reminders', label: 'Herinneringen' },
          ]}
          tabsWrapperStyle={{
            mx: { xs: 0, md: 0 },
          }}
        >
          <FormTab tab="main">
            <FormInput name="name" label="Naam" md={12} required />
            {/* <FormInput name="slug" label="Slug" md={6} required /> */}
            <SelectInput
              name="type"
              label="Type"
              md={4}
              required
              items={[
                { value: 'complex', text: 'Complex' },
                { value: 'non-complex', text: 'Niet complex' },
              ]}
            />
            <DateInput name="deadline" label="Deadline" md={4} required />
          </FormTab>
          <FormTab tab="users">
            <Grid
              xs={12}
              sx={{ height: 400, display: 'flex', flexDirection: 'column' }}
            >
              <FormGetter
                names={['id']}
                render={(values) =>
                  !!values.id && (
                    <>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                      >
                        <Typography variant="h6">Gebruikers</Typography>
                        <Button
                          variant="text"
                          color="primary"
                          startIcon={<Add />}
                          onClick={async () => {
                            const result = await showPrompt({
                              title: 'Gebruiker toevoegen',
                              form: (
                                <>
                                  <AutocompleteInput
                                    name="pb"
                                    mode="memory"
                                    label="PB"
                                    options={[
                                      {
                                        value: 'niet-complex',
                                        text: 'Niet-complex',
                                      },
                                      { value: 'complex', text: 'Complex' },
                                    ]}
                                    required
                                  />
                                </>
                              ),
                              accept: 'Toevoegen',
                              width: 500,
                            });

                            if (!result) {
                              return;
                            }

                            const clients = await hasura.request({
                              source: 'client',
                              type: 'query',
                              selection: ['id'],
                              where: {
                                _not: {
                                  forms: {
                                    webFormId: { _eq: values.id },
                                  },
                                },
                                pb: { _eq: result.pb },
                              },
                            });

                            await hasura.request({
                              source: 'clientWebForm',
                              type: 'mutation',
                              action: 'insert',
                              items: clients.map((x: any) => ({
                                clientId: x.id,
                                webFormId: values.id,
                              })),
                            });

                            usersTableRef.current?.reload();
                            tableRef.current?.reload();

                            showAlert('Gebruiker toegevoegd', 'success');
                          }}
                        >
                          Toevoegen gebruiker
                        </Button>
                        <Button
                          variant="text"
                          color="primary"
                          startIcon={<RefreshIcon />}
                          sx={{ ml: 'auto' }}
                          onClick={() => {
                            usersTableRef.current?.reload();
                          }}
                        >
                          Vernieuwen
                        </Button>
                      </Box>
                    </>
                  )
                }
              />
              <FormGetter
                names={['id']}
                render={(values) =>
                  values.id && (
                    <WebFormUsersTable
                      ref={usersTableRef}
                      id="web-form-users-table"
                      webFormId={values.id}
                      sx={{ mt: 1, flex: 1 }}
                    />
                  )
                }
              />
            </Grid>
          </FormTab>
          <FormTab tab="reminders">
            <Grid xs={12}>
              <Typography variant="h6">[Email] Uitnodiging</Typography>
            </Grid>
            <DateInput name="invitationDate" label="Datum" md={4} required />
            <FormInput
              name="invitationSubject"
              label="Onderwerp"
              md={8}
              required
            />
            <FormInput
              name="invitationText"
              label="Tekst"
              md={12}
              required
              multiline
              rows={6}
              helperText="Gebruik placeholders zoals {firstName}, {lastName}, {link} en {button} voor gepersonaliseerde inhoud."
            />
            <Grid xs={12}>
              <Typography variant="h6">[Email] Herinneringen 1</Typography>
            </Grid>
            <DateInput name="reminder1Date" label="Datum" md={4} required />
            <FormInput
              name="reminder1Subject"
              label="Onderwerp"
              md={8}
              required
            />
            <FormInput
              name="reminder1Text"
              label="Tekst"
              md={12}
              required
              multiline
              rows={3}
              helperText="Gebruik placeholders zoals {firstName}, {lastName}, {link} en {button} voor gepersonaliseerde inhoud."
            />
            <Grid xs={12}>
              <Typography variant="h6">[Email] Herinneringen 2</Typography>
            </Grid>
            <DateInput name="reminder2Date" label="Datum" md={4} required />
            <FormInput
              name="reminder2Subject"
              label="Onderwerp"
              md={8}
              required
            />
            <FormInput
              name="reminder2Text"
              label="Tekst"
              md={12}
              required
              multiline
              rows={3}
              helperText="Gebruik placeholders zoals {firstName}, {lastName}, {link} en {button} voor gepersonaliseerde inhoud."
            />
            <Grid xs={12}>
              <Typography variant="h6">[SMS] Herinneringen 3</Typography>
            </Grid>
            <DateInput name="reminder3Date" label="Datum" md={4} required />
            <FormInput
              name="reminder3Text"
              label="Tekst"
              md={12}
              required
              multiline
              rows={3}
              helperText="Gebruik placeholders zoals {firstName}, {lastName} en {link} voor gepersonaliseerde inhoud."
            />
          </FormTab>
        </FormTabs>
      </DataTableEx>
    );
  },
);
